import { Box, Typography } from "@material-ui/core";
import { UserOrg, bottomDrawerState } from "../../../store/layout";
import { TableTypes } from "../../../components/LyfeTable/LyfeDataTable";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { activeOrganizationTabState, organizationSettingsTabState, userPersonalInfoState } from "../../../store/user";
import LyfeTabs from "../../../components/lyfe-tabs/LyfeTabs";
import { BottomDrawerUserButtons } from "../personal-tab/PersonalTab";
import { UsersTab } from "./users/UsersTab";
import { DomainsTab } from "./domains/DomainsTab";
import { useHistory, useParams } from "react-router";
import { useEffect } from "react";
import { LiteBox } from "../../../components/lite/LiteBox";
import { useTranslation } from "react-i18next";
import useOrganizationStyles from "./use-organization-styles";
import SpecificErrorPage from "../../../components/SpecificErrorPage";
import { Role } from "../../../models/identity/role";
import { isGlobalOrProductUser } from "./domains/helpers";

enum Tab {
	Users = "users",
	Domains = "domains",
}

const OrganizationSettings = (props: { userOrg: UserOrg }) => {
	const { tab }: { tab: Tab | undefined } = useParams();
	const { push } = useHistory();
	const [activeTab, setActiveTab] = useRecoilState(activeOrganizationTabState);
	const { t } = useTranslation();
	const { pageName } = useOrganizationStyles();
	const bottomDrawer = useSetRecoilState(bottomDrawerState);
	const user = useRecoilValue(userPersonalInfoState);
	const isAdmin = user.role === Role.Administrator;
	const canAccessDomains = isGlobalOrProductUser(user.domains) && isAdmin;

	const tabItems: Array<{ id: TableTypes; label: string; component: JSX.Element }> = [
		{
			id: Tab.Users,
			label: t("organization-settings-tab-users"),
			component: <UsersTab userOrg={props.userOrg} canAccessDomains={canAccessDomains} />,
		},
		...(canAccessDomains
			? [
					{
						id: Tab.Domains,
						label: t("organization-settings-tab-domains"),
						component: <DomainsTab />,
					},
			  ]
			: []),
	];

	useEffect(() => {
		if (tab) {
			const tabIndex = tabItems.findIndex((i) => i.id === tab);
			if (tabIndex >= 0) {
				setActiveTab(tabIndex);
			}

			return;
		}

		setActiveTab(0);
	}, [tab, tabItems]);

	if (tab === Tab.Domains && !canAccessDomains) {
		return <SpecificErrorPage statusCode="403" />;
	}

	const handleClickInTab = (_: React.MouseEvent): void => {
		if (!activeTab) return;
		if (activeTab !== organizationSettingsTabState) {
			bottomDrawer(null);

			return;
		}
		bottomDrawer(<BottomDrawerUserButtons />);
	};

	const handleTabClick = (tabId: number) => {
		push(`/organization-settings/${tabItems[tabId].id}`);
		setActiveTab(tabId);
	};

	return (
		<Box onClickCapture={handleClickInTab}>
			<LiteBox py={3}>
				<Typography className={pageName}>{t("organization-settings")}</Typography>
			</LiteBox>
			<LyfeTabs bottomMargin={80} tabs={tabItems} tabState={activeOrganizationTabState} onTabClick={handleTabClick} />
		</Box>
	);
};

export default OrganizationSettings;
